import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Brand from '../../../types/brand/Brand';
import BrandWriteDTO from '../../../types/brand/BrandWriteDTO';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class BrandService {

    private RESOURCE: string = 'brand';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches the brands from the backend.
     *
     * @param search The search query to filter the brands by.
     * @param page The page number to fetch.
     * @param size The number of elements to fetch per page.
     * @return The brands.
     */
    public async getBrands(search?: string, page: number = 0, size: number = 10): Promise<Page<Brand>> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
        if (search) {
            params = params.set('search', search);
        }

        return await this.baseService.get<Page<Brand>>(this.RESOURCE, params);
    }

    /**
     * Fetches multiple brands from the backend.
     *
     * @param ids The IDs of the brands to fetch.
     * @return The brands.
     */
    public async getMultipleBrands(ids: number[]): Promise<Brand[]> {
        const params = new HttpParams().set('ids', ids.join(','));

        return await this.baseService.get<Brand[]>(this.RESOURCE + '/multiple', params);
    }

    /**
     * Fetches all brands from the backend.
     *
     * @return The brands.
     */
    public async getAllBrands(): Promise<Brand[]> {
        return await this.baseService.get<Brand[]>(this.RESOURCE + '/all');
    }

    /**
     * Fetches a brand from the backend.
     *
     * @param id The ID of the brand to fetch.
     * @return The brand.
     */
    public async getBrand(id: number): Promise<Brand> {
        return await this.baseService.get<Brand>(this.RESOURCE + '/' + id);
    }

    /**
     * Creates a brand in the backend.
     *
     * @param brand The brand to create.
     * @return The created brand.
     */
    public async createBrand(brand: BrandWriteDTO): Promise<void> {
        return await this.baseService.post<void>(this.RESOURCE, brand);
    }

    /**
     * Updates a brand in the backend.
     *
     * @param brand The brand to update.
     */
    public async updateBrand(brand: BrandWriteDTO): Promise<void> {
        return await this.baseService.put<void>(this.RESOURCE + '/' + brand.id, brand);
    }

    /**
     * Deletes a brand in the backend.
     *
     * @param id The ID of the brand to delete.
     */
    public async deleteBrand(id: number): Promise<void> {
        return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
    }
}
